// Glass Morphism Theme Configuration
// 配色方案：纯黑背景 + 玻璃态效果 + 赛博朋克科技感

export const glassTheme = {
  // 基础颜色
  colors: {
    // 主背景色
    background: '#000000',
    // 文字颜色 - 提高对比度
    text: {
      primary: '#ffffff',      // 纯白，最高对比
      secondary: '#d1d5db',    // 浅灰，仍然高对比
      muted: '#9ca3af',        // 中灰，用于次要信息
    },
    // 强调色 - 提高亮度
    accent: {
      blue: '#60a5fa',         // 更亮的蓝色
      purple: '#c084fc',       // 更亮的紫色
      green: '#4ade80',        // 更亮的绿色
      yellow: '#facc15',       // 更亮的黄色
      pink: '#f472b6',         // 更亮的粉色
      cyan: '#22d3ee',         // 更亮的青色
    },
    // 状态颜色 - 提高亮度
    status: {
      success: '#4ade80',      // 亮绿色
      error: '#f87171',        // 亮红色
      warning: '#facc15',      // 亮黄色
      info: '#60a5fa',         // 亮蓝色
    },
    // 边框颜色 - 提高可见度
    border: 'rgba(255, 255, 255, 0.2)',
    borderHover: 'rgba(255, 255, 255, 0.4)',
  },

  // 玻璃态效果 - 增强边框可见度
  glass: {
    // 容器样式
    container: `
      background: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 16px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
    `,
    // 轻玻璃效果
    light: `
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 16px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    `,
    // 重玻璃效果
    heavy: `
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border: 1px solid rgba(255, 255, 255, 0.25);
      border-radius: 16px;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
    `,
  },

  // 网格背景 - 增强网格可见度
  gridBackground: `
    background-color: #000000;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
    background-size: 40px 40px;
    background-position: center center;
    box-shadow: inset 0 0 100px rgba(255, 255, 255, 0.02);
  `,

  // 间距
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },

  // 圆角
  borderRadius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    full: '9999px',
  },

  // 阴影
  shadow: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.5)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
    glow: `
      0 0 20px rgba(59, 130, 246, 0.3),
      0 0 40px rgba(168, 85, 247, 0.2),
      0 0 60px rgba(34, 197, 94, 0.1);
    `,
  },

  // 过渡动画
  transition: {
    fast: '150ms ease-in-out',
    base: '300ms ease-in-out',
    slow: '500ms ease-in-out',
  },

  // 渐变色 - 提高亮度
  gradients: {
    primary: 'linear-gradient(135deg, #60a5fa 0%, #c084fc 100%)',
    success: 'linear-gradient(135deg, #4ade80 0%, #22d3ee 100%)',
    warning: 'linear-gradient(135deg, #facc15 0%, #fb923c 100%)',
    error: 'linear-gradient(135deg, #f87171 0%, #f472b6 100%)',
    cyber: 'linear-gradient(135deg, #60a5fa 0%, #c084fc 50%, #22d3ee 100%)',
  },
};

// Styled Components 混合宏
export const glassMixins = {
  container: `
    ${glassTheme.glass.container}
    box-shadow: ${glassTheme.shadow.md};
    transition: all ${glassTheme.transition.base};

    &:hover {
      border-color: ${glassTheme.colors.borderHover};
      box-shadow: ${glassTheme.shadow.lg};
    }
  `,

  button: `
    background: ${glassTheme.gradients.primary};
    border: none;
    border-radius: ${glassTheme.borderRadius.md};
    color: #ffffff;
    padding: 12px 24px;
    font-weight: 700;
    cursor: pointer;
    transition: all ${glassTheme.transition.base};
    box-shadow: ${glassTheme.shadow.md};

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
      filter: brightness(1.1);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `,

  input: `
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid ${glassTheme.colors.border};
    border-radius: ${glassTheme.borderRadius.md};
    color: #ffffff;
    padding: 12px 16px;
    font-size: 16px;
    font-weight: 500;
    transition: all ${glassTheme.transition.base};

    &::placeholder {
      color: ${glassTheme.colors.text.secondary};
    }

    &:focus {
      outline: none;
      border-color: ${glassTheme.colors.accent.blue};
      box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
      background: rgba(0, 0, 0, 0.7);
    }

    &:hover {
      border-color: ${glassTheme.colors.borderHover};
    }
  `,

  modal: `
    ${glassTheme.glass.heavy}
    box-shadow: ${glassTheme.shadow.xl};
  `,
};

export default glassTheme;
